import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  MAX_INGESTION_SUMMARIES,
  type SessionIngestionSummary,
  waitingFileCount,
} from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

const PENDING_POLL_INTERVAL_MS = 15_000;

/**
 * Each session's ingestion in counts, keyed by session id, for the first 100
 * of `sessionIds`: one request for a list's page. Refetched every 15 seconds
 * while any of them waits for a file whose ingestion hasn't failed.
 */
export function useSessionIngestionSummaries(
  sessionIds: readonly string[],
  { enabled = true, orgId }: { enabled?: boolean; orgId?: string } = {},
) {
  // Read the service only when fetching: lists in orgs without session ingestion never fetch.
  const services = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const ids = sessionIds.slice(0, MAX_INGESTION_SUMMARIES);
  return useQuery({
    queryKey: sessionQueryKeys.ingestionSummaries(resolvedOrgId, ids),
    queryFn: async ({ signal }) => {
      const summaries = await services.sessionService.getIngestionSummaries(
        ids,
        resolvedOrgId,
        { abortSignal: signal },
      );
      return new Map<string, SessionIngestionSummary>(
        summaries.map((summary) => [summary.session_id, summary]),
      );
    },
    enabled: enabled && ids.length > 0,
    refetchInterval: (query) =>
      [...(query.state.data?.values() ?? [])].some(
        (summary) => waitingFileCount(summary) > 0,
      )
        ? PENDING_POLL_INTERVAL_MS
        : false,
  });
}
